var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Flow, FlowSchema, FlowSelectActionResolver, FlowSelectActionName, evaluateSchema, } from '.';
import { Action, ActionSetName, Exit, Question, QuestionShouldContinueOnFail, Trigger, QuestionEvaluate, } from '../Actions';
import { completeInteraction, handleTrigger, incrementFlowAction, startQuestion, updateContext, addQuestionAttempt, createSmsCookie, } from '../SmsCookie';
import { getMockTwilioWebhookRequest, } from '../twllio';
import { compose, deepCopy } from '../util';
export function pipeSmsCookieUpdates(...funcs) {
    return (cookie = createSmsCookie(getMockTwilioWebhookRequest())) => compose(...funcs)(cookie);
}
const EXIT_REGEXP = /(\b)(exit)(\b)/i;
export const defaultTestForExit = ((body) => EXIT_REGEXP.test(body));
const defaultOptions = {
    onInteractionEnd: () => null,
    testForExit: defaultTestForExit,
};
export default class FlowController {
    constructor(root, schema, { onInteractionEnd = defaultOptions.onInteractionEnd, testForExit = defaultOptions.testForExit, } = defaultOptions) {
        if (!(root instanceof Flow)) {
            throw new TypeError('root parameter must be an instance of Flow');
        }
        if (root.length === 0) {
            throw new TypeError('All Flows must perform at least one action. Check the root Flow');
        }
        if (schema && !(schema instanceof FlowSchema)) {
            throw new TypeError('schema parameter must be an instance of FlowSchema');
        }
        if (typeof testForExit !== 'function') {
            throw new TypeError('testForExit parameter must be a function');
        }
        // other type checking here
        this.root = root;
        if (schema) {
            // DFS of schema to get each user-defined flow
            // uniqueness of flow names is enforced by the fact that JS objects cannot have duplicate keys
            // each Flow instance can only be used once in the schema
            this.schema = evaluateSchema(root, schema);
            if (this.schema.size === 1) {
                throw new TypeError('If you provide the schema parameter, it must include a flow distinct from the root Flow');
            }
        }
        this.onInteractionEnd = onInteractionEnd;
        this.testForExit = testForExit;
    }
    getCurrentFlow(state) {
        if ((!state.flow) ||
            (state.flow === this.root.name)) {
            return this.root;
        }
        if (!(this.schema && this.schema.has(state.flow))) {
            throw new TypeError(`Received invalid flow name in SMS cookie: ${state.flow}`);
        }
        return this.schema.get(state.flow);
    }
    resolveActionFromState(req, state, userCtx) {
        return __awaiter(this, void 0, void 0, function* () {
            if (state.isComplete) {
                return null;
            }
            if (this.testForExit && (yield this.testForExit(req.body.Body))) {
                return new Exit(req.body.Body);
            }
            const key = Number(state.flowKey);
            const currFlow = this.getCurrentFlow(state);
            const resolveAction = currFlow[FlowSelectActionResolver](key);
            if (!resolveAction) {
                return null;
            }
            const action = yield resolveAction(deepCopy(state.flowContext), userCtx);
            if (action instanceof Question) {
                yield action[QuestionEvaluate](req, state);
            }
            if (!(action instanceof Action)) {
                return null;
            }
            action[ActionSetName](currFlow[FlowSelectActionName](key));
            return action;
        });
    }
    resolveNextStateFromAction(req, state, action) {
        const currFlow = this.getCurrentFlow(state);
        if (!(action instanceof Action)) {
            return completeInteraction(state);
        }
        if (action instanceof Exit) {
            return pipeSmsCookieUpdates((s) => updateContext(s, currFlow, action), completeInteraction)(state);
        }
        switch (action.constructor) {
            case Question:
                return (() => {
                    const question = action;
                    if (state.question.isAnswering) {
                        state = addQuestionAttempt(state, req.body.Body);
                    }
                    if (question.isAnswered) {
                        return pipeSmsCookieUpdates((s) => updateContext(s, currFlow, action), (s) => incrementFlowAction(s, currFlow))(state);
                    }
                    if (question.isFailed) {
                        if (question[QuestionShouldContinueOnFail]) {
                            return pipeSmsCookieUpdates((s) => updateContext(s, currFlow, action), (s) => incrementFlowAction(s, currFlow))(state);
                        }
                        return pipeSmsCookieUpdates((s) => updateContext(s, currFlow, action), completeInteraction)(state);
                    }
                    if (state.question.isAnswering) {
                        return updateContext(state, currFlow, action);
                    }
                    return pipeSmsCookieUpdates(startQuestion, (s) => updateContext(s, currFlow, action))(state);
                })();
            case Trigger:
                return (() => {
                    const trigger = action;
                    if ((currFlow === this.root) && (!this.schema)) {
                        throw new Error('Cannot use Trigger action without a defined Flow schema');
                    }
                    if (!(trigger.flowName === this.root.name
                        || this.schema.has(trigger.flowName))) {
                        throw new Error('Trigger constructors expect a name of an existing Flow');
                    }
                    return pipeSmsCookieUpdates((s) => updateContext(s, currFlow, action), (s) => handleTrigger(s, trigger))(state);
                })();
            default:
                return pipeSmsCookieUpdates((s) => updateContext(s, currFlow, action), (s) => incrementFlowAction(s, currFlow))(state);
        }
    }
}
